'use client';

import { useCallback } from 'react';
import { Icon } from './Icon';

export interface VBCContactActionsProps {
  vendorId: string | number;
  email: string | null;
  website: string | null;
  /** Called when a contact action is clicked (email, website). App handles tracking. */
  onTrackInteraction?: (type: string, vendorId: string | number) => void;
}

/**
 * Action buttons for the VBC detail page -- Email, Website.
 * Each button invokes the onTrackInteraction callback for the app to handle tracking.
 */
export function VBCContactActions({
  vendorId,
  email,
  website,
  onTrackInteraction,
}: VBCContactActionsProps) {
  const handleTrack = useCallback(
    (type: 'phone_click' | 'email_click' | 'website_click') => {
      onTrackInteraction?.(type, vendorId);
    },
    [vendorId, onTrackInteraction],
  );

  const hasActions = email || website;
  if (!hasActions) return null;

  return (
    <div className="flex flex-wrap gap-3">
      {email && (
        <a
          href={`mailto:${email}`}
          onClick={() => handleTrack('email_click')}
          className="inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-all duration-150"
          style={{
            borderColor: 'var(--brand-primary, #014F8A)',
            color: 'var(--brand-primary, #014F8A)',
            backgroundColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget;
            el.style.backgroundColor = 'var(--brand-primary, #014F8A)';
            el.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget;
            el.style.backgroundColor = 'transparent';
            el.style.color = 'var(--brand-primary, #014F8A)';
          }}
        >
          <Icon name="alternate_email" size={16} />
          Email
        </a>
      )}

      {website && (
        <a
          href={website.startsWith('http') ? website : `https://${website}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleTrack('website_click')}
          className="inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-all duration-150"
          style={{
            borderColor: 'var(--brand-primary, #014F8A)',
            color: 'var(--brand-primary, #014F8A)',
            backgroundColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget;
            el.style.backgroundColor = 'var(--brand-primary, #014F8A)';
            el.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget;
            el.style.backgroundColor = 'transparent';
            el.style.color = 'var(--brand-primary, #014F8A)';
          }}
        >
          <Icon name="open_in_new" size={16} />
          Website
        </a>
      )}
    </div>
  );
}

export interface VBCContactInfoProps {
  contactName?: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  phone: string | null;
  cellPhone: string | null;
  website: string | null;
  vendorId: string | number;
  /** Called when a contact link is clicked. App handles tracking. */
  onTrackInteraction?: (type: string, vendorId: string | number) => void;
}

/**
 * Contact information display for the VBC detail page.
 * Shows address, phone numbers, and website with tracking callbacks.
 */
export function VBCContactInfo({
  contactName,
  address,
  city,
  state,
  zipCode,
  phone,
  cellPhone,
  website,
  vendorId,
  onTrackInteraction,
}: VBCContactInfoProps) {
  const handleTrack = useCallback(
    (type: 'phone_click' | 'website_click') => {
      onTrackInteraction?.(type, vendorId);
    },
    [vendorId, onTrackInteraction],
  );

  const fullAddress = [address, city, state, zipCode].filter(Boolean).join(', ');
  const hasContact = fullAddress || phone || cellPhone || website;

  if (!hasContact) return null;

  return (
    <div className="space-y-3">
      {contactName && (
        <div className="flex items-start gap-2.5">
          <Icon
            name="person"
            size={16}
            className="mt-0.5 flex-shrink-0"
            style={{ color: 'var(--brand-input-placeholder)' }}
          />
          <span
            className="text-sm font-medium"
            style={{ color: 'var(--brand-text)' }}
          >
            {contactName}
          </span>
        </div>
      )}
      {fullAddress && (
        <div className="flex items-start gap-2.5">
          <Icon
            name="language"
            size={16}
            className="mt-0.5 flex-shrink-0"
            style={{ color: 'var(--brand-input-placeholder)' }}
          />
          <span
            className="text-sm"
            style={{ color: 'var(--brand-text-muted)' }}
          >
            {fullAddress}
          </span>
        </div>
      )}

      {phone && (
        <div className="flex items-center gap-2.5">
          <Icon
            name="call"
            size={16}
            className="flex-shrink-0"
            style={{ color: 'var(--brand-input-placeholder)' }}
          />
          <a
            href={`tel:${phone}`}
            onClick={() => handleTrack('phone_click')}
            className="text-sm underline-offset-2 hover:underline"
            style={{ color: 'var(--brand-primary, #014F8A)' }}
          >
            {phone}
          </a>
        </div>
      )}

      {cellPhone && (
        <div className="flex items-center gap-2.5">
          <Icon
            name="call"
            size={16}
            className="flex-shrink-0"
            style={{ color: 'var(--brand-input-placeholder)' }}
          />
          <a
            href={`tel:${cellPhone}`}
            onClick={() => handleTrack('phone_click')}
            className="text-sm underline-offset-2 hover:underline"
            style={{ color: 'var(--brand-primary, #014F8A)' }}
          >
            {cellPhone} (cell)
          </a>
        </div>
      )}

      {website && (
        <div className="flex items-center gap-2.5">
          <Icon
            name="language"
            size={16}
            className="flex-shrink-0"
            style={{ color: 'var(--brand-input-placeholder)' }}
          />
          <a
            href={website.startsWith('http') ? website : `https://${website}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleTrack('website_click')}
            className="text-sm underline-offset-2 hover:underline"
            style={{ color: 'var(--brand-primary, #014F8A)' }}
          >
            {website.replace(/^https?:\/\//, '')}
          </a>
        </div>
      )}
    </div>
  );
}
